/**
 * FoodPulse Admin — one-page dashboard for site operators.
 *
 * Layout: left sidebar with 7 tabs (Overview, Users, Businesses,
 * Products, Deals, Claims, Submissions, Reports) + a right-side main
 * pane. Every tab lazy-loads its own data on click, so the initial
 * paint stays fast even with 60k+ businesses.
 *
 * Auth: relies on the shared `useAuth` hook — non-admins get a
 * "Access denied" screen. All API calls hit `/api/foodpulse/admin/*`
 * which are `current_admin` gated on the backend.
 *
 * Route: /food-pulse/admin
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Activity, Building2, ChartLine, ChefHat, Coins, FileText,
  Loader2, Search, Store, Tags, Users as UsersIcon,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { key: "overview",    label: "Overview",     icon: ChartLine },
  { key: "users",       label: "Users",        icon: UsersIcon },
  { key: "businesses",  label: "Businesses",   icon: Building2 },
  { key: "products",    label: "Products",     icon: Store },
  { key: "deals",       label: "Deals",        icon: Tags },
  { key: "claims",      label: "Claims",       icon: FileText },
  { key: "submissions", label: "Submissions",  icon: ChefHat },
  { key: "reports",     label: "Reports",      icon: Coins },
];

// Token is auto-attached by AuthContext via axios.defaults.headers, so
// we just need the URL prefix here.
async function apiGet(path, _token, params = {}) {
  const res = await axios.get(`${API}/api/foodpulse/admin${path}`, { params });
  return res.data;
}

// ─────────────────── KPI card ──────────────────────────────────────────
function Kpi({ label, value, hint, testid }) {
  return (
    <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: "#E9D8B4" }} data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-widest opacity-60" style={{ color: "#8B5E10" }}>
        {label}
      </div>
      <div className="mt-1 font-display text-3xl leading-none" style={{ color: "#1A1613" }}>
        {(value ?? 0).toLocaleString()}
      </div>
      {hint && <div className="mt-1 text-[11px] opacity-70" style={{ color: "#3E2A0A" }}>{hint}</div>}
    </div>
  );
}

// ─────────────────── Data table shell ─────────────────────────────────
function DataTable({ columns, rows, loading, empty }) {
  if (loading) {
    return (
      <div className="p-12 text-center text-sm opacity-70">
        <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" /> Loading…
      </div>
    );
  }
  if (!rows?.length) {
    return <div className="p-12 text-center text-sm opacity-70">{empty || "No records found."}</div>;
  }
  return (
    <div className="overflow-x-auto rounded-2xl border bg-white" style={{ borderColor: "#E9D8B4" }}>
      <table className="w-full text-sm">
        <thead className="text-[10px] font-mono uppercase tracking-widest" style={{ background: "#FBF3E0", color: "#8B5E10" }}>
          <tr>{columns.map((c) => (
            <th key={c.key} className="text-left px-3 py-2 whitespace-nowrap">{c.label}</th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || r.slug || i} className="border-t" style={{ borderColor: "#F0E2C7" }}>
              {columns.map((c) => (
                <td key={c.key} className="px-3 py-2 align-top" style={{ color: "#3E2A0A" }}>
                  {c.render ? c.render(r) : (r[c.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Pagination({ skip, limit, total, onPage }) {
  const page = Math.floor(skip / limit) + 1;
  const pages = Math.max(1, Math.ceil(total / limit));
  return (
    <div className="flex items-center justify-between mt-3 text-xs" style={{ color: "#3E2A0A" }}>
      <div className="opacity-70">
        {total.toLocaleString()} total · page {page} / {pages}
      </div>
      <div className="flex gap-2">
        <button className="px-3 py-1 rounded border disabled:opacity-40" style={{ borderColor: "#E9D8B4" }}
                disabled={page <= 1} onClick={() => onPage(Math.max(0, skip - limit))}
                data-testid="admin-prev-page">← Prev</button>
        <button className="px-3 py-1 rounded border disabled:opacity-40" style={{ borderColor: "#E9D8B4" }}
                disabled={page >= pages} onClick={() => onPage(skip + limit)}
                data-testid="admin-next-page">Next →</button>
      </div>
    </div>
  );
}

/** Download an admin table as a CSV via the streaming export endpoint. */
function ExportCsvButton({ resource, q }) {
  const download = () => {
    const url = new URL(`${API}/api/foodpulse/admin/export/${resource}.csv`);
    if (q) url.searchParams.set("q", q);
    url.searchParams.set("limit", "5000");
    // Anchor download — auth header travels via cookies/axios defaults
    // aren't attached to native <a> clicks, so we go through fetch and
    // build a blob URL. Keeps the Authorization Bearer intact.
    (async () => {
      try {
        const res = await axios.get(url.toString(), { responseType: "blob" });
        const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
        const dl = document.createElement("a");
        dl.href = window.URL.createObjectURL(blob);
        dl.download = `foodpulse-${resource}.csv`;
        document.body.appendChild(dl);
        dl.click();
        dl.remove();
        toast.success(`Exported ${resource} to CSV`);
      } catch { toast.error("Couldn't export — check your permissions."); }
    })();
  };
  return (
    <button onClick={download}
            className="px-3 py-2 rounded-lg border text-xs font-mono uppercase tracking-wider hover:bg-white"
            style={{ borderColor: "#E9D8B4", color: "#3E2A0A" }}
            data-testid={`admin-export-${resource}`}
            title={`Download ${resource} as CSV`}>
      ⬇ Export CSV
    </button>
  );
}

// ─────────────────── Overview tab ─────────────────────────────────────
function OverviewTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    apiGet("/overview", token).then((d) => { if (alive) setData(d); })
      .catch(() => toast.error("Couldn't load overview"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [token]);

  if (loading || !data) return <div className="p-8 text-sm opacity-70 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading KPIs…</div>;

  const artByCat = Object.entries(data.articles?.by_category || {})
    .sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-8" data-testid="admin-overview">
      <section>
        <h2 className="font-display text-2xl mb-3" style={{ color: "#1A1613" }}>Users</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi label="Total" value={data.users.total} testid="kpi-users-total" />
          <Kpi label="New · 7d" value={data.users.new_7d} testid="kpi-users-7d" />
          <Kpi label="New · 30d" value={data.users.new_30d} testid="kpi-users-30d" />
          <Kpi label="Verified" value={data.users.verified} testid="kpi-users-verified" />
          <Kpi label="Admins" value={data.users.admins} testid="kpi-users-admin" />
        </div>
      </section>

      <section>
        <h2 className="font-display text-2xl mb-3" style={{ color: "#1A1613" }}>Businesses & Brands</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Businesses" value={data.businesses.total} testid="kpi-biz-total" />
          <Kpi label="Claimed" value={data.businesses.claimed}
               hint={`${data.businesses.total ? Math.round(100 * data.businesses.claimed / data.businesses.total) : 0}% of listings`}
               testid="kpi-biz-claimed" />
          <Kpi label="Pending claims" value={data.businesses.pending_claims} testid="kpi-biz-pending" />
          <Kpi label="Brands" value={data.brands.total} testid="kpi-brands-total" />
        </div>
      </section>

      <section>
        <h2 className="font-display text-2xl mb-3" style={{ color: "#1A1613" }}>Content coverage</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Food items" value={data.food_items.total}
               hint={`${data.food_items.draft} draft · ${data.food_items.curated} curated`}
               testid="kpi-items-total" />
          <Kpi label="Recipes" value={data.recipes.total}
               hint={`${data.recipes.no_instructions} missing steps`}
               testid="kpi-recipes-total" />
          <Kpi label="Articles" value={data.articles.total}
               hint={artByCat.slice(0, 3).map(([k, v]) => `${k}:${v}`).join(" · ")}
               testid="kpi-articles-total" />
          <Kpi label="Health articles"
               value={data.articles.by_category?.health || 0}
               testid="kpi-articles-health" />
        </div>
      </section>

      <section>
        <h2 className="font-display text-2xl mb-3" style={{ color: "#1A1613" }}>Marketplace</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Products" value={data.products.total} testid="kpi-products-total" />
          <Kpi label="Deals" value={data.deals.total} testid="kpi-deals-total" />
          <Kpi label="Submissions" value={data.submissions.total} testid="kpi-submissions-total" />
          <Kpi label="Total claims" value={data.claims.total}
               hint={`${data.claims.otp_pending} pending · ${data.claims.verified} verified`}
               testid="kpi-claims-total" />
        </div>
      </section>
    </div>
  );
}

// ─────────────────── Reusable paginated tab shell ─────────────────────
function usePagedTable(path, token, extraParams = {}) {
  const [state, setState] = useState({ items: [], total: 0, skip: 0, limit: 25, loading: true });
  const paramsKey = JSON.stringify(extraParams);
  const load = useCallback((skip = 0) => {
    setState((s) => ({ ...s, loading: true }));
    apiGet(path, token, { ...extraParams, skip, limit: state.limit })
      .then((d) => setState((s) => ({ ...s, ...d, loading: false })))
      .catch(() => { toast.error("Load failed"); setState((s) => ({ ...s, loading: false })); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, token, paramsKey]);
  useEffect(() => { load(0); }, [load]);
  return { ...state, reload: load };
}

// ─────────────────── Users tab ────────────────────────────────────────
function UsersTab({ token }) {
  const [q, setQ] = useState("");
  const [verified, setVerified] = useState("");
  const [isAdmin, setIsAdmin] = useState("");
  const params = useMemo(() => {
    const p = {};
    if (q) p.q = q;
    if (verified) p.verified = verified === "true";
    if (isAdmin) p.is_admin = isAdmin === "true";
    return p;
  }, [q, verified, isAdmin]);
  const t = usePagedTable("/users", token, params);

  const cols = [
    { key: "name",   label: "Name",     render: (r) => <div>
        <div className="font-medium" style={{ color: "#1A1613" }}>{r.name || "—"}</div>
        <div className="text-[11px] opacity-60">@{r.handle || "—"}</div>
      </div> },
    { key: "email",  label: "Email",    render: (r) => <span className="font-mono text-[12px]">{r.email}</span> },
    { key: "role",   label: "Role",     render: (r) => <span className="text-[11px] font-mono uppercase tracking-wider">
        {r.is_admin ? "admin" : (r.role || "user")}
      </span> },
    { key: "email_verified", label: "Verified",
      render: (r) => r.email_verified ? "✓" : "—" },
    { key: "city",   label: "City",     render: (r) => `${r.city || ""}${r.country ? ", " + r.country : ""}` },
    { key: "created_at", label: "Signed up",
      render: (r) => r.created_at ? new Date(r.created_at).toLocaleDateString() : "—" },
  ];

  return (
    <div data-testid="admin-users-tab">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-2.5 opacity-40" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search email, name, handle or phone…"
                 className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm"
                 style={{ borderColor: "#E9D8B4" }}
                 data-testid="admin-users-search" />
        </div>
        <select value={verified} onChange={(e) => setVerified(e.target.value)}
                className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "#E9D8B4" }}
                data-testid="admin-users-verified">
          <option value="">All verify states</option>
          <option value="true">Verified only</option>
          <option value="false">Unverified only</option>
        </select>
        <select value={isAdmin} onChange={(e) => setIsAdmin(e.target.value)}
                className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "#E9D8B4" }}
                data-testid="admin-users-role">
          <option value="">All roles</option>
          <option value="true">Admins only</option>
        </select>
        <ExportCsvButton resource="users" q={q} />
      </div>
      <DataTable columns={cols} rows={t.items} loading={t.loading} empty="No users match your filters." />
      <Pagination skip={t.skip} limit={t.limit} total={t.total} onPage={t.reload} />
    </div>
  );
}

// ─────────────────── Businesses tab ───────────────────────────────────
function BusinessesTab({ token }) {
  const [q, setQ] = useState("");
  const [claim, setClaim] = useState("");
  const [state, setState] = useState("");
  const params = useMemo(() => {
    const p = {};
    if (q) p.q = q;
    if (claim) p.claim_status = claim;
    if (state) p.state_code = state.toUpperCase();
    return p;
  }, [q, claim, state]);
  const t = usePagedTable("/businesses", token, params);

  const cols = [
    { key: "name", label: "Business", render: (r) =>
        <Link to={`/food-pulse/businesses/${r.slug}`} className="hover:underline" style={{ color: "#8B5E10" }}
              data-testid={`biz-link-${r.slug}`}>{r.name}</Link> },
    { key: "business_type", label: "Type", render: (r) => r.business_type || "—" },
    { key: "state_code", label: "State", render: (r) => `${r.city || ""} ${r.state_code || ""}` },
    { key: "claim_status", label: "Claim",
      render: (r) => <span className="text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded"
                            style={{ background: r.claim_status === "claimed" ? "#DCFCE7"
                                                : r.claim_status === "pending" ? "#FEF3C7" : "#F3F4F6",
                                     color:      r.claim_status === "claimed" ? "#166534"
                                                : r.claim_status === "pending" ? "#92400E" : "#374151" }}>
          {r.claim_status || "unclaimed"}
        </span> },
    { key: "likes_count", label: "Likes", render: (r) => r.likes_count || 0 },
    { key: "created_at", label: "Added",
      render: (r) => r.created_at ? new Date(r.created_at).toLocaleDateString() : "—" },
  ];

  return (
    <div data-testid="admin-businesses-tab">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-2.5 opacity-40" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search name, slug or city…"
                 className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm" style={{ borderColor: "#E9D8B4" }}
                 data-testid="admin-biz-search" />
        </div>
        <select value={claim} onChange={(e) => setClaim(e.target.value)}
                className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "#E9D8B4" }}
                data-testid="admin-biz-claim">
          <option value="">All claim states</option>
          <option value="claimed">Claimed</option>
          <option value="pending">Pending</option>
          <option value="unclaimed">Unclaimed</option>
        </select>
        <input value={state} onChange={(e) => setState(e.target.value)}
               placeholder="State (e.g. NY)" maxLength={2}
               className="w-24 px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "#E9D8B4" }}
               data-testid="admin-biz-state" />
        <ExportCsvButton resource="businesses" q={q} />
      </div>
      <DataTable columns={cols} rows={t.items} loading={t.loading} empty="No businesses match your filters." />
      <Pagination skip={t.skip} limit={t.limit} total={t.total} onPage={t.reload} />
    </div>
  );
}

// ─────────────────── Products / Deals / Claims / Submissions ──────────
function ProductsTab({ token }) {
  const [q, setQ] = useState("");
  const params = useMemo(() => q ? { q } : {}, [q]);
  const t = usePagedTable("/products", token, params);
  const cols = [
    { key: "name", label: "Product" },
    { key: "business_name", label: "Business" },
    { key: "price", label: "Price", render: (r) => r.price != null ? `${r.currency || ""}${r.price}` : "—" },
    { key: "stock", label: "Stock", render: (r) => r.stock ?? "—" },
    { key: "is_active", label: "Active", render: (r) => r.is_active ? "✓" : "—" },
    { key: "created_at", label: "Added", render: (r) => r.created_at ? new Date(r.created_at).toLocaleDateString() : "—" },
  ];
  return (
    <div data-testid="admin-products-tab">
      <div className="relative mb-4 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-2.5 opacity-40" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search product or business name…"
                 className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm" style={{ borderColor: "#E9D8B4" }}
                 data-testid="admin-products-search" />
        </div>
        <ExportCsvButton resource="products" q={q} />
      </div>
      <DataTable columns={cols} rows={t.items} loading={t.loading} empty="No products yet — try inviting sellers to list." />
      <Pagination skip={t.skip} limit={t.limit} total={t.total} onPage={t.reload} />
    </div>
  );
}

function DealsTab({ token }) {
  const t = usePagedTable("/deals", token, {});
  const cols = [
    { key: "title", label: "Deal" },
    { key: "business_name", label: "Business" },
    { key: "price_original", label: "Was" },
    { key: "price_deal", label: "Now" },
    { key: "status", label: "Status" },
    { key: "created_at", label: "Posted", render: (r) => r.created_at ? new Date(r.created_at).toLocaleDateString() : "—" },
  ];
  return (
    <div data-testid="admin-deals-tab">
      <div className="flex justify-end mb-3"><ExportCsvButton resource="deals" /></div>
      <DataTable columns={cols} rows={t.items} loading={t.loading}
                 empty="No deals posted yet. When sellers post surplus / clearance offers they will appear here." />
      <Pagination skip={t.skip} limit={t.limit} total={t.total} onPage={t.reload} />
    </div>
  );
}

function ClaimsTab({ token }) {
  const [status, setStatus] = useState("");
  const [actionOn, setActionOn] = useState(null);   // claim being acted on
  const params = useMemo(() => status ? { status } : {}, [status]);
  const t = usePagedTable("/claims", token, params);

  const decide = async (claim, verb) => {
    const notes = window.prompt(`Optional notes for ${verb}ing this claim:`, "") || "";
    setActionOn(claim.id);
    try {
      await axios.post(`${API}/api/foodpulse/admin/claims/${claim.id}/action`,
                       { action: verb, notes });
      toast.success(`Claim ${verb}${verb.endsWith("e") ? "d" : "ed"}`);
      t.reload(t.skip);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Couldn't ${verb} claim`);
    } finally {
      setActionOn(null);
    }
  };

  const cols = [
    { key: "business_name", label: "Business",
      render: (r) => <Link to={`/food-pulse/businesses/${r.business_slug}`} className="hover:underline" style={{ color: "#8B5E10" }}>
          {r.business_name || r.business_slug}
        </Link> },
    { key: "claimant_name", label: "Claimant",
      render: (r) => <div>
        <div className="font-medium">{r.claimant_name}</div>
        <div className="text-[11px] font-mono opacity-70">{r.claimant_email}</div>
      </div> },
    { key: "claimant_role", label: "Role" },
    { key: "status", label: "Status",
      render: (r) => <span className="text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded"
                            style={{ background: r.status === "verified" || r.status === "approved" ? "#DCFCE7"
                                                : r.status === "otp_pending" ? "#FEF3C7"
                                                : r.status === "rejected" ? "#FEE2E2" : "#F3F4F6",
                                     color:      r.status === "verified" || r.status === "approved" ? "#166534"
                                                : r.status === "otp_pending" ? "#92400E"
                                                : r.status === "rejected" ? "#991B1B" : "#374151" }}>
          {r.status}
        </span> },
    { key: "created_at", label: "Submitted",
      render: (r) => r.created_at ? new Date(r.created_at).toLocaleString() : "—" },
    { key: "actions", label: "Actions",
      render: (r) => {
        const busy = actionOn === r.id;
        const done = r.status === "approved" || r.status === "rejected";
        return (
          <div className="flex gap-1.5">
            <button onClick={() => decide(r, "approve")} disabled={busy || done || r.status === "otp_pending"}
                    className="px-2 py-1 rounded text-[11px] font-semibold text-white disabled:opacity-40"
                    style={{ background: "#166534" }}
                    data-testid={`claim-approve-${r.id}`}>
              {busy ? "…" : "Approve"}
            </button>
            <button onClick={() => decide(r, "reject")} disabled={busy || done}
                    className="px-2 py-1 rounded text-[11px] font-semibold text-white disabled:opacity-40"
                    style={{ background: "#991B1B" }}
                    data-testid={`claim-reject-${r.id}`}>
              {busy ? "…" : "Reject"}
            </button>
          </div>
        );
      } },
  ];
  return (
    <div data-testid="admin-claims-tab">
      <div className="flex items-center gap-3 mb-4">
        <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "#E9D8B4" }}
                data-testid="admin-claims-status">
          <option value="">All statuses</option>
          <option value="otp_pending">Pending OTP</option>
          <option value="verified">Verified</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <ExportCsvButton resource="claims" />
      </div>
      <DataTable columns={cols} rows={t.items} loading={t.loading} empty="No claims yet." />
      <Pagination skip={t.skip} limit={t.limit} total={t.total} onPage={t.reload} />
    </div>
  );
}

function SubmissionsTab({ token }) {
  const [kind, setKind] = useState("all");
  const params = useMemo(() => ({ kind }), [kind]);
  const t = usePagedTable("/submissions", token, params);
  const cols = [
    { key: "_label", label: "Type",
      render: (r) => <span className="text-[11px] font-mono uppercase tracking-wider">{r._kind}</span> },
    { key: "name", label: "Subject",
      render: (r) => r.name || r.title || r.subject || r.slug || "—" },
    { key: "submitted_by_email", label: "Submitted by",
      render: (r) => r.submitted_by_email || r.email || "—" },
    { key: "created_at", label: "When",
      render: (r) => r.created_at ? new Date(r.created_at).toLocaleString() : "—" },
  ];
  return (
    <div data-testid="admin-submissions-tab">
      <div className="flex items-center gap-3 mb-4">
        <select value={kind} onChange={(e) => setKind(e.target.value)}
                className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "#E9D8B4" }}
                data-testid="admin-subs-kind">
          <option value="all">All types</option>
          <option value="product">Product suggestions</option>
          <option value="recipe">Recipe suggestions</option>
          <option value="public">Public submissions</option>
          <option value="photo">Photo submissions</option>
        </select>
      </div>
      <DataTable columns={cols} rows={t.items} loading={t.loading} empty="No submissions yet." />
      <Pagination skip={t.skip} limit={t.limit} total={t.total} onPage={t.reload} />
    </div>
  );
}

// ─────────────────── Reports tab ──────────────────────────────────────
function Sparkline({ series, testid }) {
  if (!series?.length) return null;
  const w = 320, h = 44, pad = 4;
  const max = Math.max(1, ...series.map((p) => p.count));
  const xStep = (w - pad * 2) / Math.max(1, series.length - 1);
  const points = series.map((p, i) => [
    pad + i * xStep,
    h - pad - (p.count / max) * (h - pad * 2),
  ]);
  const d = "M " + points.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(" L ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" data-testid={testid}>
      <path d={d} fill="none" stroke="#8B5E10" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      {points.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="1.5" fill="#8B5E10" opacity="0.6" />
      ))}
    </svg>
  );
}

function ReportsTab({ token }) {
  const [growth, setGrowth] = useState(null);
  const [geo, setGeo] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [g, ge, c] = await Promise.all([
          apiGet("/reports/growth", token, { days: 30 }),
          apiGet("/reports/geo", token, { limit: 15 }),
          apiGet("/reports/content", token),
        ]);
        if (alive) { setGrowth(g); setGeo(ge); setContent(c); }
      } catch { toast.error("Report load failed"); }
      finally { alive && setLoading(false); }
    })();
    return () => { alive = false; };
  }, [token]);

  if (loading) return <div className="p-8 text-sm opacity-70 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Building reports…</div>;

  return (
    <div className="space-y-8" data-testid="admin-reports-tab">
      {growth && (
        <section>
          <h2 className="font-display text-2xl mb-3" style={{ color: "#1A1613" }}>30-day growth</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              ["users", "New user signups"],
              ["businesses", "Businesses added"],
              ["claims", "Business claim submissions"],
              ["products", "Product listings"],
            ].map(([k, label]) => {
              const series = growth[k] || [];
              const total = series.reduce((a, p) => a + (p.count || 0), 0);
              return (
                <div key={k} className="rounded-2xl border p-4 bg-white" style={{ borderColor: "#E9D8B4" }} data-testid={`report-growth-${k}`}>
                  <div className="flex items-baseline justify-between mb-1">
                    <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#8B5E10" }}>{label}</div>
                    <div className="font-display text-lg" style={{ color: "#1A1613" }}>{total.toLocaleString()}</div>
                  </div>
                  <Sparkline series={series} testid={`spark-${k}`} />
                </div>
              );
            })}
          </div>
        </section>
      )}

      {geo && (
        <section>
          <h2 className="font-display text-2xl mb-3" style={{ color: "#1A1613" }}>Geographic coverage</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: "#E9D8B4" }} data-testid="report-geo-states">
              <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#8B5E10" }}>Top states</div>
              <ul className="space-y-1 text-sm">
                {geo.states?.map((s) => (
                  <li key={s.state_code} className="flex items-center justify-between">
                    <span className="font-mono">{s.state_code}</span>
                    <span className="opacity-70">{s.total.toLocaleString()} · {s.claimed_pct}% claimed</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: "#E9D8B4" }} data-testid="report-geo-metros">
              <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#8B5E10" }}>Top metros</div>
              <ul className="space-y-1 text-sm">
                {geo.metros?.map((m) => (
                  <li key={m.metro_area} className="flex items-center justify-between">
                    <span className="truncate mr-2">{m.metro_area}</span>
                    <span className="opacity-70">{m.total.toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: "#E9D8B4" }} data-testid="report-geo-types">
              <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#8B5E10" }}>Business types</div>
              <ul className="space-y-1 text-sm">
                {geo.types?.map((t) => (
                  <li key={t.business_type} className="flex items-center justify-between">
                    <span className="truncate mr-2">{t.business_type}</span>
                    <span className="opacity-70">{t.total.toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {content && (
        <section>
          <h2 className="font-display text-2xl mb-3" style={{ color: "#1A1613" }}>Content coverage</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ["food_items", "Food items"],
              ["recipes", "Recipes"],
              ["brands", "Brands"],
              ["businesses", "Businesses"],
            ].map(([k, label]) => {
              const c = content[k];
              const pct = c.curated_pct ?? 0;
              return (
                <div key={k} className="rounded-2xl border p-4 bg-white" style={{ borderColor: "#E9D8B4" }} data-testid={`report-content-${k}`}>
                  <div className="text-[10px] font-mono uppercase tracking-widest opacity-60" style={{ color: "#8B5E10" }}>{label}</div>
                  <div className="mt-1 font-display text-2xl" style={{ color: "#1A1613" }}>{pct}%</div>
                  <div className="text-[11px] opacity-70 mt-0.5">
                    {c.total?.toLocaleString?.() ?? c.total} total
                    {c.ai_seeded != null && <> · {c.ai_seeded} AI-seeded</>}
                  </div>
                  <div className="mt-2 h-1.5 rounded-full" style={{ background: "#F0E2C7" }}>
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "#8B5E10" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

// ─────────────────── Activity strip (below sidebar on Overview) ───────
function ActivityStrip({ token }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    apiGet("/activity", token, { limit: 8 }).then((d) => setItems(d.items || [])).catch(() => {});
  }, [token]);
  if (!items.length) return null;
  return (
    <aside className="rounded-2xl border p-4 bg-white sticky top-4" style={{ borderColor: "#E9D8B4" }} data-testid="admin-activity">
      <div className="text-[10px] font-mono uppercase tracking-widest mb-3 flex items-center gap-1.5" style={{ color: "#8B5E10" }}>
        <Activity className="w-3.5 h-3.5" /> Recent activity
      </div>
      <ol className="space-y-3 text-sm">
        {items.map((e, i) => (
          <li key={i} className="pb-3 border-b last:border-b-0" style={{ borderColor: "#F0E2C7" }}>
            <div className="text-[10px] font-mono uppercase tracking-widest opacity-60" style={{ color: "#8B5E10" }}>
              {e.kind}
            </div>
            {e.link
              ? <Link to={e.link} className="hover:underline block" style={{ color: "#3E2A0A" }}>{e.title}</Link>
              : <div style={{ color: "#3E2A0A" }}>{e.title}</div>}
            {e.subtitle && <div className="text-[11px] opacity-70">{e.subtitle}</div>}
            <div className="text-[10px] opacity-50 mt-0.5">
              {e.at ? new Date(e.at).toLocaleString() : ""}
            </div>
          </li>
        ))}
      </ol>
    </aside>
  );
}

// ─────────────────── Root page ────────────────────────────────────────
export default function FoodPulseAdmin() {
  const { user, loading: authLoading } = useAuth() || {};
  const token = null; // axios auto-attaches via AuthContext defaults
  const [tab, setTab] = useState("overview");

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center text-sm opacity-60">
      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Checking access…
    </div>;
  }
  if (!user || !user.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center" style={{ background: "#FBF7F0" }}>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#B25D38" }}>Restricted</div>
          <h1 className="font-display text-3xl mb-2" style={{ color: "#1A1613" }}>Admin access required</h1>
          <p className="text-sm opacity-80 mb-4" style={{ color: "#3E2A0A" }}>
            You need a FoodPulse admin account to view this dashboard.
          </p>
          <Link to="/food-pulse" className="text-sm underline" style={{ color: "#8B5E10" }} data-testid="admin-denied-home">
            ← Back to Food Pulse
          </Link>
        </div>
      </div>
    );
  }

  const activeTab = TABS.find((t) => t.key === tab) || TABS[0];

  const renderTab = () => {
    switch (tab) {
      case "overview":    return <OverviewTab   token={token} />;
      case "users":       return <UsersTab      token={token} />;
      case "businesses":  return <BusinessesTab token={token} />;
      case "products":    return <ProductsTab   token={token} />;
      case "deals":       return <DealsTab      token={token} />;
      case "claims":      return <ClaimsTab     token={token} />;
      case "submissions": return <SubmissionsTab token={token} />;
      case "reports":     return <ReportsTab    token={token} />;
      default:            return null;
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#FBF7F0" }} data-testid="foodpulse-admin">
      {/* Header */}
      <header className="border-b" style={{ background: "#F0E2C7", borderColor: "#E9D8B4" }}>
        <div className="max-w-7xl mx-auto px-4 py-6 flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#8B5E10" }}>
              — Food Pulse · admin
            </div>
            <h1 className="font-display text-3xl md:text-4xl leading-none mt-1" style={{ color: "#1A1613" }}>
              Operator dashboard
            </h1>
          </div>
          <Link to="/food-pulse" className="text-[11px] font-mono uppercase tracking-widest opacity-70 hover:opacity-100 hover:underline"
                style={{ color: "#3E2A0A" }} data-testid="admin-back-home">
            ← Site home
          </Link>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 md:grid md:grid-cols-[220px_1fr_260px] md:gap-6">
        {/* Sidebar */}
        <nav className="mb-4 md:mb-0" data-testid="admin-sidebar">
          <div className="rounded-2xl border overflow-hidden bg-white sticky top-4" style={{ borderColor: "#E9D8B4" }}>
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button key={t.key} onClick={() => setTab(t.key)}
                        data-testid={`admin-tab-${t.key}`}
                        className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-left border-b last:border-b-0 transition-colors ${active ? "font-semibold" : ""}`}
                        style={{
                          borderColor: "#F0E2C7",
                          background: active ? "#8B5E10" : "transparent",
                          color:      active ? "white"   : "#3E2A0A",
                        }}>
                  <Icon className="w-4 h-4" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Main */}
        <main className="min-w-0">
          <div className="mb-4">
            <h2 className="font-display text-2xl" style={{ color: "#1A1613" }}>{activeTab.label}</h2>
          </div>
          {renderTab()}
        </main>

        {/* Right rail — activity feed */}
        <div className="hidden md:block">
          <ActivityStrip token={token} />
        </div>
      </div>
    </div>
  );
}
